/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import {
  GoogleMap,
  InfoWindow,
  LoadScript,
  Marker,
  MarkerClusterer,
} from "@react-google-maps/api";
import LinearProgress from "@mui/material/LinearProgress";
import { FleetDashboardOverviewApi } from "../../../../API/Iot-Api/FleetDashboardOverview/FleetDashobardOverview";
import {
  AllFleetLocationListResponse,
  AllFleetLocationResponse,
} from "../../../../@Types/DRO/IotApiResponse/FleetOverviewResponse";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationSearchingIcon from "@mui/icons-material/LocationSearching";
import PermIdentityOutlinedIcon from '@mui/icons-material/PermIdentityOutlined';
const containerStyle = {
  width: "100%",
  height: "100%",
};

interface MapProps {
  searchQrCode: string | null;
  category: string;
}

const GoogleMapApi: React.FC<MapProps> = ({ searchQrCode, category }) => {
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [selectedMarker, setSelectedMarker] = useState<number | null>(null);
  const [fleetSummary, setFleetSummary] =
    useState<AllFleetLocationResponse | null>(null);
  const [allMarkers, setAllMarkers] = useState<
    ({ lat: number; lng: number } & Omit<
      AllFleetLocationListResponse,
      "lng" | "lat"
    >)[]
  >([]);
  const [center, setCenter] = useState<
    { lat: number; lng: number } | undefined
  >(undefined);
  const [markersToShow, setMarkersToShow] = useState<typeof allMarkers>([]);
  const mapRef = useRef<google.maps.Map | null>(null);

  const calculateCenter = () => {
    // Check if fleetSummary and fleetSummary.data are defined
    if (
      !fleetSummary?.data?.records ||
      fleetSummary.data.records.length === 0
    ) {
      console.error("No data available");
      return { lat: 0, lng: 0 }; // Default center
    }

    const mid = Math.floor(fleetSummary.data.records.length / 2);
    const centerMarker = fleetSummary.data.records[mid];
    console.log("centerMarker", centerMarker);
    return {
      lat: parseFloat(centerMarker?.lat || "0"), // Default to "0" if lat is undefined
      lng: parseFloat(centerMarker?.lng || "0"), // Default to "0" if lng is undefined
    };
  };
  function getAllMarkersOfCurrentBound() {
    const mapBounds = mapRef.current?.getBounds();
    return setMarkersToShow(
      allMarkers.filter((currentMarker) =>
        mapBounds?.contains(
          new google.maps.LatLng(currentMarker.lat, currentMarker.lng)
        )
      )
    );
  }

  useEffect(() => {
    const fetchAllFleetLocation = async () => {
      setLoading(true);
      setMessage(null); // Reset message before fetching

      try {
        const data: AllFleetLocationResponse =
          await FleetDashboardOverviewApi.api.getAllFleetLocation(
            searchQrCode,
            category
          );

        if (data?.data?.records?.length) {
          const tempMarkers: typeof allMarkers = data.data?.records
            ?.filter((item) => category === "total" || item.status === category)
            .map((e) => ({
              ...e,
              lat: parseFloat(e.lat || "0"),
              lng: parseFloat(e.lng || "0"),
              status: e.status,
              qrCode: e.qrCode,
              time: e.time,
            }));

          setFleetSummary(data);
          setAllMarkers(tempMarkers);
        } else {
          setLoading(false);
          setFleetSummary(null);
          setMessage("No Data Found");
        }
      } catch (error) {
        console.error("Error fetching fleet summary", error);
        setFleetSummary(null);
        setMessage("Error fetching data");
      } finally {
        setLoading(false);
      }
    };
    fetchAllFleetLocation();
  }, [searchQrCode, category]);
  useEffect(() => {
    setCenter(calculateCenter());
  }, [allMarkers]);

  const getCustomMarkerIcon = (status: string) => {
    return `https://zypp.app/clientdashboard-mailer/${status.replace(
      / /g,
      "_"
    )}.svg`;
  };

  const handleMarkerClick = (index: number) => {
    setSelectedMarker(index);
  };
  const navigateToQRCodePage = (qrCode: string) => {
    // Save the qrCode temporarily
    sessionStorage.setItem("fleetTrackingQrCode", qrCode);
    // Open the new page
    window.open("/fleet-tracking", "_blank");
  };
  
  return (
    <>
      {loading && (
        <div className="w-full">
          <LinearProgress
            sx={{
              backgroundColor: "white",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#00CD5B",
              },
            }}
          />
        </div>
      )}
      {!loading && message ? (
        <div className="text-center text-gray-500 font-bold pt-4">
          {message}
        </div>
      ) : (
        <GoogleMap
          onBoundsChanged={getAllMarkersOfCurrentBound}
          mapContainerStyle={containerStyle}
          center={center}
          zoom={8}
          // onZoomChanged={() => {
          //   setSelectedMarker(null);
          // }}
          onClick={(event) => {
            setSelectedMarker(null);
          }}
          onLoad={(map) => {
            mapRef.current = map;
          }}
        >
          <MarkerClusterer
            averageCenter
            enableRetinaIcons
            gridSize={60}
            options={{
              minimumClusterSize: 2,
              maxZoom: 15,
              batchSize: 10,
            }}
          >
            {(clusterer) => (
              <>
                {allMarkers.map((item, index) => {
                  const customIconUrl = getCustomMarkerIcon(item.status);
                  return (
                    <Marker
                      key={index}
                      position={{
                        lat: item.lat,
                        lng: item.lng,
                      }}
                      icon={{ url: customIconUrl }}
                      onClick={() => handleMarkerClick(index)}
                      clusterer={clusterer}
                    />
                  );
                })}
              </>
            )}
          </MarkerClusterer>
          {selectedMarker !== null && (
            <InfoWindow
              position={{
                lat: parseFloat(
                  fleetSummary?.data.records[selectedMarker]?.lat || "0"
                ),
                lng: parseFloat(
                  fleetSummary?.data.records[selectedMarker]?.lng || "0"
                ),
              }}
              onCloseClick={() => setSelectedMarker(null)}
            >
              <div className="flex items-center">
                <div>
                  <p
                    className={"p-1 font-bold text-left text-slate-700 flex items-center"}
                  >
                    <span className="mr-2">
                      <FiberManualRecordIcon sx={{ color: "#06B681" }} />
                    </span>
                    <b
                      className="cursor-pointer text-blue-400"
                      onClick={() =>
                        navigateToQRCodePage(
                          allMarkers[selectedMarker]?.qrCode ?? ""
                        )
                      }
                    >
                      {allMarkers[selectedMarker]?.qrCode}
                    </b>
                  </p>
                  <p  className={"p-1 font-bold text-left text-slate-700 flex items-center"} >
                    <span className="mr-2">
                      <PermIdentityOutlinedIcon sx={{ color: "#06B681" }} />
                    </span>
                    <b>{allMarkers[selectedMarker]?.userId}</b>
                  </p>
                  <p
                    className={"p-1 font-bold text-left text-slate-700 flex items-center"}
                  >
                    <span className="mr-2">
                      <AccessTimeIcon sx={{ color: "#06B681" }} />
                    </span>
                    <b>{allMarkers[selectedMarker]?.time}</b>
                  </p>
                  <p className="p-1 font-medium text-left text-slate-700 flex items-center mt-2">
                    <span className="mr-2">
                      <LocationSearchingIcon sx={{ color: "#06B681" }} />
                    </span>
                    {allMarkers[selectedMarker]?.lat} ,{" "}
                    {allMarkers[selectedMarker]?.lng}
                  </p>
                </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      )}
    </>
  );
};

export default GoogleMapApi;